import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Archive,
  BookOpen,
  ChevronDown,
  CircleDollarSign,
  Command as CommandIcon,
  Gauge,
  HelpCircle,
  LayoutDashboard,
  Moon,
  PanelTop,
  Power,
  Settings,
  Shield,
  Sun,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { clearAdminToken } from '@/api/adminApi'
import type { CreditsBalance } from '@/api/creditsApi'
import { creditsApi } from '@/api/creditsApi'
import { healthApi } from '@/api/healthApi'
import { ApiError, friendlyMessage, setOnUnauthorized } from '@/api/http'
import { sessionApi } from '@/api/sessionApi'
import { queryClient } from '@/app/queryClient'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { APP_VERSION_LABEL } from '@/lib/appVersion'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@/components/ui/command'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import { formatNumber } from '@/utils/format'

const NAV_ITEMS = [
  { key: '/home', labelKey: 'nav.home', icon: LayoutDashboard },
  { key: '/projects', labelKey: 'nav.projects', icon: BookOpen },
  { key: '/trash', labelKey: 'nav.trash', icon: Trash2 },
  { key: '/board', labelKey: 'nav.board', icon: PanelTop },
  { key: '/library', labelKey: 'nav.library', icon: Archive },
  { key: '/billing', labelKey: 'nav.billing', icon: CircleDollarSign },
  { key: '/consistency', labelKey: 'nav.consistency', icon: Gauge },
  { key: '/admin', labelKey: 'nav.admin', icon: Shield },
] as const

export function resolveCreditsDisplay(
  balance: Pick<CreditsBalance, 'enabled' | 'balance'> | undefined,
  isBusy: boolean,
  t: (key: string) => string,
) {
  if (balance?.enabled === false) {
    return { value: 'unlimited', title: t('shell.creditsUnlimited') }
  }
  if (isBusy) {
    return { value: null, title: undefined }
  }
  if (balance?.enabled === true && typeof balance.balance === 'number') {
    return { value: formatNumber(balance.balance), title: undefined }
  }
  return { value: null, title: undefined }
}

export function CreditsInfinity({ label }: { label: string }) {
  return <span className="nf-infinity" aria-label={label} role="img" />
}

function BackendHealthDot() {
  const { t } = useTranslation()
  const healthQuery = useQuery({
    queryKey: ['backend-health', 'readyz'],
    queryFn: healthApi.ready,
    refetchInterval: 15_000,
    retry: false,
  })
  const isHealthy = healthQuery.data?.status === 'ready' && !healthQuery.isError
  const checkedAt = healthQuery.dataUpdatedAt || healthQuery.errorUpdatedAt
  const checkedAtLabel = checkedAt ? new Date(checkedAt).toLocaleTimeString() : t('shell.healthUnchecked')
  const statusLabel = isHealthy ? t('shell.healthNormal') : t('shell.healthAbnormal')

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="flex size-8 items-center justify-center rounded-md"
          role="status"
          aria-label={t('shell.healthTooltip', { status: statusLabel })}
        >
          <span
            className={`size-2.5 rounded-full ${isHealthy ? 'bg-emerald-500 shadow-[0_0_0_3px_rgb(16_185_129_/_0.16)]' : 'bg-destructive shadow-[0_0_0_3px_rgb(239_68_68_/_0.14)]'}`}
            aria-hidden
          />
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          <p>{t('shell.healthTooltip', { status: statusLabel })}</p>
          <p className="text-[11px] opacity-80">{t('shell.healthCheckedAt', { time: checkedAtLabel })}</p>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function AppLoading() {
  return (
    <div className="grid min-h-screen place-items-center bg-background">
      <div className="space-y-3">
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-3 w-28" />
      </div>
    </div>
  )
}

function AppError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="grid min-h-screen place-items-center bg-background p-6">
      <div className="max-w-sm rounded-lg border bg-card p-6 text-center shadow-sm">
        <p className="text-base font-semibold text-foreground">{t('common.serviceError')}</p>
        <p className="mt-2 text-sm text-muted-foreground">{t('shell.bootstrapFailed')}</p>
        <Button className="mt-5" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      </div>
    </div>
  )
}

function ChangePasswordDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation()
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const resetForm = () => {
    setOldPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setError(null)
    setIsSubmitting(false)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) resetForm()
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)

    if (!oldPassword || !newPassword || !confirmPassword) {
      setError(t('account.passwordRequired'))
      return
    }
    if (newPassword !== confirmPassword) {
      setError(t('account.passwordMismatch'))
      return
    }

    setIsSubmitting(true)
    try {
      await sessionApi.changePassword(oldPassword, newPassword)
      toast.success(t('account.passwordChangeSuccess'))
      handleOpenChange(false)
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 404
          ? t('account.passwordUnavailable')
          : friendlyMessage(err, t('account.passwordChangeFailed'))
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={onSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('account.changePassword')}</DialogTitle>
            <DialogDescription>{t('account.changePasswordDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <label htmlFor="current-password" className="text-sm font-medium">
                {t('account.currentPassword')}
              </label>
              <Input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={oldPassword}
                onChange={(event) => setOldPassword(event.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="new-password" className="text-sm font-medium">
                {t('account.newPassword')}
              </label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="confirm-password" className="text-sm font-medium">
                {t('account.confirmPassword')}
              </label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('account.changingPassword') : t('account.changePassword')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default function AppShell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, status, logout, bootstrap, clearSession } = useAuthStore()
  const { mode, toggleMode } = useUiStore()
  const [commandOpen, setCommandOpen] = useState(false)
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)

  useEffect(() => {
    setOnUnauthorized(() => {
      const authState = useAuthStore.getState()
      if (authState.status === 'guest' || !authState.user) return
      clearSession()
      clearAdminToken()
      queryClient.clear()
      navigate('/login', { replace: true })
    })
    return () => setOnUnauthorized(null)
  }, [clearSession, navigate])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setCommandOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const creditsQuery = useQuery({
    queryKey: ['credits', 'balance', user?.user_id],
    queryFn: () => creditsApi.balance(user?.user_id),
    enabled: status === 'authed',
    staleTime: 60_000,
  })

  const selected = useMemo(
    () => NAV_ITEMS.find((item) => location.pathname.startsWith(item.key))?.key ?? '/home',
    [location.pathname],
  )

  if (status === 'unknown') return <AppLoading />
  if (status === 'guest') return <Navigate to="/login" replace state={{ from: location.pathname }} />
  if (status === 'error') return <AppError onRetry={() => void bootstrap()} />

  const creditsDisplay = resolveCreditsDisplay(creditsQuery.data, creditsQuery.isLoading || creditsQuery.isFetching, t)

  const runCommand = async (action: () => void | Promise<void>) => {
    setCommandOpen(false)
    await action()
  }

  const onLogout = async () => {
    await logout()
    queryClient.clear()
    navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto flex h-14 max-w-[1600px] items-center gap-3 px-4 sm:px-6">
          <Link to="/home" className="flex min-w-0 items-center gap-2 text-sm font-semibold">
            <span className="nf-pulse size-2.5 rounded-full bg-primary" aria-hidden />
            <span className="truncate text-primary">{t('app.name')}</span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.slice(0, 7).map((item) => {
              const Icon = item.icon
              const active = selected === item.key
              return (
                <Button key={item.key} asChild variant={active ? 'secondary' : 'ghost'} size="sm">
                  <Link to={item.key}>
                    <Icon className="size-4" />
                    {t(item.labelKey)}
                  </Link>
                </Button>
              )
            })}
          </nav>

          <div className="ml-auto flex items-center gap-1.5">
            <BackendHealthDot />

            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="hidden h-8 shrink-0 items-center gap-2 whitespace-nowrap rounded-md border bg-card px-3 text-sm sm:flex"
                  title={creditsDisplay.title}
                >
                  <CircleDollarSign className="size-4 text-primary" />
                  <span className="text-muted-foreground">{t('shell.credits')}</span>
                  {creditsDisplay.value === null ? (
                    <Skeleton className="h-3 w-12" />
                  ) : creditsDisplay.value === 'unlimited' ? (
                    <CreditsInfinity label={t('shell.creditsUnlimitedLabel')} />
                  ) : (
                    <span className="nf-mono">{creditsDisplay.value}</span>
                  )}
                </div>
              </TooltipTrigger>
              {creditsDisplay.title ? <TooltipContent>{creditsDisplay.title}</TooltipContent> : null}
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" onClick={toggleMode} aria-label={t('shell.toggleTheme')}>
                  {mode === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('shell.toggleTheme')}</TooltipContent>
            </Tooltip>

            <LanguageSwitcher />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button asChild variant="ghost" size="icon-sm" aria-label={t('shell.helpManual')}>
                  <a href="/manual">
                    <HelpCircle className="size-4" />
                  </a>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('shell.helpManual')}</TooltipContent>
            </Tooltip>

            <Button variant="outline" size="sm" className="hidden gap-2 sm:inline-flex" onClick={() => setCommandOpen(true)}>
              <CommandIcon className="size-4" />
              {t('shell.command')}
              <span className="nf-mono text-xs text-muted-foreground">{t('shell.commandShortcut')}</span>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 max-w-[11rem] gap-2 border bg-card px-2 shadow-none hover:bg-accent/60"
                  aria-label={t('shell.userMenu')}
                >
                  <Avatar name={user?.username} className="size-6 text-xs" />
                  <span className="hidden min-w-0 max-w-24 truncate sm:inline">{user?.username ?? t('shell.currentUser')}</span>
                  <ChevronDown className="size-3.5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="py-2">
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar name={user?.username} className="size-9 text-sm" />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-sm font-medium">{user?.username ?? t('shell.currentUser')}</span>
                        {user?.is_admin ? (
                          <Badge
                            variant="secondary"
                            className="border border-[var(--nf-ember-soft)] bg-[var(--nf-ember-soft)] px-1.5 py-0 text-[10px] text-[var(--nf-ember)]"
                          >
                            {t('shell.adminBadge')}
                          </Badge>
                        ) : null}
                      </div>
                      <span className="truncate text-xs font-normal text-muted-foreground">{user?.user_id}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate('/billing')}>
                  <CircleDollarSign className="size-4" />
                  {t('shell.menuBilling')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setPasswordDialogOpen(true)}>
                  <Settings className="size-4" />
                  {t('shell.menuAccountSettings')}
                </DropdownMenuItem>
                {user?.is_admin ? (
                  <DropdownMenuItem onSelect={() => navigate('/admin')}>
                    <Shield className="size-4" />
                    {t('shell.menuAdmin')}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                  onSelect={() => void onLogout()}
                >
                  <Power className="size-4" />
                  {t('common.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="nf-mono hidden h-8 select-none items-center rounded-md border border-[var(--nf-ember-soft)] bg-[var(--nf-ember-soft)] px-2 text-[11px] font-medium tracking-tight text-[var(--nf-ember)] sm:inline-flex"
                  aria-label={t('shell.appVersion')}
                >
                  {APP_VERSION_LABEL}
                </span>
              </TooltipTrigger>
              <TooltipContent>{t('shell.appVersion')}</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </header>

      <main className="mx-auto min-h-[calc(100vh-3.5rem)] max-w-[1600px]">
        <Outlet />
      </main>

      <CommandDialog
        open={commandOpen}
        onOpenChange={setCommandOpen}
        title={t('shell.commandTitle')}
        description={t('shell.commandDescription')}
      >
        <CommandInput placeholder={t('shell.commandPlaceholder')} />
        <CommandList>
          <CommandEmpty>{t('shell.commandEmpty')}</CommandEmpty>
          <CommandGroup heading={t('shell.commandNavigate')}>
            <CommandItem onSelect={() => void runCommand(() => navigate('/home'))}>
              <LayoutDashboard className="size-4" />
              {t('nav.home')}
            </CommandItem>
            <CommandItem onSelect={() => void runCommand(() => navigate('/projects'))}>
              <BookOpen className="size-4" />
              {t('nav.projects')}
            </CommandItem>
            <CommandItem onSelect={() => void runCommand(() => navigate('/board'))}>
              <PanelTop className="size-4" />
              {t('nav.board')}
            </CommandItem>
            <CommandItem onSelect={() => void runCommand(() => navigate('/library'))}>
              <Archive className="size-4" />
              {t('nav.library')}
            </CommandItem>
            <CommandItem onSelect={() => void runCommand(() => navigate('/billing'))}>
              <CircleDollarSign className="size-4" />
              {t('nav.billing')}
            </CommandItem>
            <CommandItem onSelect={() => void runCommand(() => navigate('/consistency'))}>
              <Gauge className="size-4" />
              {t('nav.consistency')}
            </CommandItem>
            <CommandItem onSelect={() => void runCommand(() => navigate('/admin'))}>
              <Shield className="size-4" />
              {t('nav.admin')}
            </CommandItem>
          </CommandGroup>
          <CommandGroup heading={t('shell.commandActions')}>
            <CommandItem onSelect={() => void runCommand(toggleMode)}>
              {mode === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
              {t('shell.toggleTheme')}
              <CommandShortcut>{mode === 'dark' ? t('shell.lightMode') : t('shell.darkMode')}</CommandShortcut>
            </CommandItem>
            <CommandItem onSelect={() => void runCommand(onLogout)}>
              <Power className="size-4" />
              {t('common.logout')}
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
      <ChangePasswordDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen} />
    </div>
  )
}
